// Executive Brain visual tokens — cluster colors, edge-weight scale, motion timings.
//
// Small, self-contained token layer that sits on top of the existing Tailwind
// dark theme (slate-900/950, emerald accents) so the Brain reads as native Cipher.
// Colors are chosen for AA contrast on the slate-950 canvas.

// The real entity clusters the neural graph can represent. Each maps to a REAL
// Cipher collection / endpoint (see brainGraphAdapter for the join keys).
export const CLUSTERS = {
  DECISION: { key: "DECISION", label: "Decision", color: "#34d399", source: "GET /api/executive/queue" },
  RECOMMENDATION: { key: "RECOMMENDATION", label: "Recommendation", color: "#38bdf8", source: "recommendations" },
  PREDICTION: { key: "PREDICTION", label: "Prediction", color: "#a78bfa", source: "predictions" },
  OUTCOME: { key: "OUTCOME", label: "Outcome", color: "#fbbf24", source: "GET /api/outcomes (Outcome Spine)" },
  GOAL: { key: "GOAL", label: "Goal", color: "#f472b6", source: "GET /api/goals" },
  OBSERVATION: { key: "OBSERVATION", label: "Observation", color: "#22d3ee", source: "GET /api/intelligence/observations" },
  EXPERIMENT: { key: "EXPERIMENT", label: "Experiment", color: "#818cf8", source: "GET /api/intelligence/experiments" },
  KNOWLEDGE: { key: "KNOWLEDGE", label: "Knowledge", color: "#fb923c", source: "GET /api/lessons" },
  MEMORY: { key: "MEMORY", label: "Memory", color: "#94a3b8", source: "GET /api/journal" },
  EVIDENCE: { key: "EVIDENCE", label: "Evidence", color: "#64748b", source: "outcome.evidence[]" },
};

export function clusterColor(key) {
  return (CLUSTERS[key] || CLUSTERS.EVIDENCE).color;
}

export function clusterLabel(key) {
  return (CLUSTERS[key] || {}).label || key;
}

// Real edge-relationship provenance. The graph NEVER draws an edge without one of
// these real join keys behind it (enforced in brainGraphAdapter).
export const EDGE_TYPES = {
  OUTCOME: { key: "OUTCOME", label: "resolved into", join: "outcome.subject_ref_id === decision.item_id" },
  GOAL: { key: "GOAL", label: "advances", join: "outcome.goal_ids[] / goal_alignment.goal_ids[]" },
  CORRELATION: { key: "CORRELATION", label: "same run", join: "shared correlation_id" },
  EVIDENCE: { key: "EVIDENCE", label: "evidenced by", join: "outcome.evidence[].ref_id" },
  MEMORY: { key: "MEMORY", label: "journaled as", join: "journal.links.recommendation_id / outcome_id" },
  HIERARCHY: { key: "HIERARCHY", label: "parent of", join: "goal ancestors()" },
};

export function edgeProvenance(type) {
  return EDGE_TYPES[type] || { key: type, label: "linked", join: "unknown" };
}

// Node radius (px) scales with a REAL count (edge degree or intrinsic count).
// Deterministic, bounded — never a random/decorative size.
export const NODE_RADIUS = { min: 10, max: 34 };
export function nodeRadius(size, maxSize) {
  const { min, max } = NODE_RADIUS;
  if (!maxSize || maxSize <= 0) return min;
  const t = Math.max(0, Math.min(1, size / maxSize));
  return Math.round(min + t * (max - min));
}

// Edge stroke width (px) scales with REAL link strength (count of underlying joins).
export const EDGE_WIDTH = { min: 1, max: 6 };
export function edgeWidth(weight, maxWeight) {
  const { min, max } = EDGE_WIDTH;
  if (!maxWeight || maxWeight <= 0) return min;
  const t = Math.max(0, Math.min(1, weight / maxWeight));
  return +(min + t * (max - min)).toFixed(2);
}

// Edge stroke opacity (0..1) — the "hot vs. dull" glow treatment — scales with the SAME
// real link-strength fraction used by edgeWidth above. High-weight edges render brighter
// (closer to max), low-weight edges stay duller (closer to min). No new concept of
// "hotness" is introduced; this reuses the real weight/maxWeight already computed for width.
export const EDGE_GLOW = { min: 0.18, max: 0.85 };
export function edgeGlow(weight, maxWeight) {
  const { min, max } = EDGE_GLOW;
  if (!maxWeight || maxWeight <= 0) return min;
  const t = Math.max(0, Math.min(1, weight / maxWeight));
  return +(min + t * (max - min)).toFixed(3);
}

// Motion: a node's pulse period is derived from its real size (bigger = more
// central = slower, calmer pulse). Motion therefore encodes data, not decoration.
// Respect prefers-reduced-motion at the component layer.
export function pulseSeconds(size, maxSize) {
  if (!maxSize || maxSize <= 0) return 4;
  const t = Math.max(0, Math.min(1, size / maxSize));
  return +(5 - t * 2.5).toFixed(2); // 5s (small) -> 2.5s (large hub)
}

// ── Legacy motion constants for DORMANT M2–M4 components (kept until those milestones
// rebuild against the semantic-motion engine in lib/motion.js). NOTE: ambient
// "breathing" has been REMOVED per Motion Bible Law #1 — there is no idle motion. ──
export const MOTION = {
  // node activation on real data load (staggered reveal, ordered by resolve).
  activationStaggerMs: 90,
  activationFadeMs: 420,
  // traversal-order path illumination along REAL edges (one stage at a time).
  illuminateStepMs: 520,
  // decision -> center zoom / background recede.
  zoomMs: 620,
  recedeBlurPx: 3,
  recedeOpacity: 0.35,
};

// Confidence ring completeness (0..1) from a real tier. Static encode of a real value.
export const TIER_FRACTION = { LOW: 0.34, MEDIUM: 0.67, HIGH: 1.0, NA: 0.0 };
export function tierFraction(tier) {
  return TIER_FRACTION[tier] != null ? TIER_FRACTION[tier] : 0.0;
}
